import type { Conversation } from '@/types/chat';

export interface UserConversationGroup {
  userId: string;
  user: {
    id: string;
    username?: string;
    email: string;
    avatar_url?: string | null;
  };
  conversations: Conversation[];
  totalCount: number;
  unreadCount: number;
  mostRecentAt: string;
}

/**
 * Groups conversations by user (member_id) and sorts them by most recent conversation
 */
export function groupConversationsByUser(conversations: Conversation[]): UserConversationGroup[] {
  // Group conversations by member_id
  const groupedByUser = conversations.reduce((groups, conversation) => {
    const userId = conversation.member_id;
    
    if (!groups[userId]) {
      groups[userId] = [];
    }
    
    groups[userId].push(conversation);
    return groups;
  }, {} as Record<string, Conversation[]>);

  // Convert to UserConversationGroup array
  const userGroups = Object.entries(groupedByUser).map(([userId, userConversations]) => {
    // Sort conversations within the group by most recent first
    const sortedConversations = userConversations.sort((a, b) => {
      const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });

    // Calculate total unread count for this user
    const unreadCount = sortedConversations.reduce((total, conv) => total + (conv.unreadCount || 0), 0);

    // Get the most recent conversation time
    const mostRecentConversation = sortedConversations[0];
    const mostRecentAt = mostRecentConversation?.last_message_at || mostRecentConversation?.created_at || '';

    // Extract user information from the first conversation
    const userInfo = mostRecentConversation?.member;

    return {
      userId,
      user: {
        id: userId,
        username: userInfo?.username,
        email: userInfo?.email || '',
        avatar_url: userInfo?.avatar_url,
      },
      conversations: sortedConversations,
      totalCount: sortedConversations.length,
      unreadCount,
      mostRecentAt,
    };
  });

  // Sort user groups by most recent conversation
  return userGroups.sort((a, b) => {
    const aTime = a.mostRecentAt ? new Date(a.mostRecentAt).getTime() : 0;
    const bTime = b.mostRecentAt ? new Date(b.mostRecentAt).getTime() : 0;
    return bTime - aTime;
  });
}

/**
 * Gets visible conversations for a user group based on pagination
 */
export function getVisibleConversations(
  group: UserConversationGroup, 
  visibleCount: number = 3
): Conversation[] {
  return group.conversations.slice(0, visibleCount);
}

/**
 * Calculates remaining conversations for a user group
 */
export function getRemainingCount(
  group: UserConversationGroup, 
  visibleCount: number = 3
): number {
  return Math.max(0, group.totalCount - visibleCount);
}

/**
 * Gets the display name for a user
 */
export function getUserDisplayName(user: UserConversationGroup['user']): string {
  return user.username || user.email || 'User';
}

/**
 * Gets initials for a user avatar fallback
 */
export function getUserInitials(user: UserConversationGroup['user']): string {
  const displayName = getUserDisplayName(user);
  return displayName
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}